import { onBeforeMount, watch } from 'vue';
import { useSearchParams, encodeValuePreservingCommas } from '@/composables/useSearchParams.js';

/**
 * Syncs a Pinia store's filter keys to the URL query string (bookmarkable/shareable) and to
 * sessionStorage (survives a hard `window.location` reload, e.g. statements' Airwallex SCA
 * re-init, which wipes the Pinia store entirely). On mount, URL params win if present and also
 * clear `shouldResetFilters`; otherwise falls back to the sessionStorage snapshot.
 *
 * Doesn't sync `search_text`/pagination/sort - those have their own reset behavior.
 *
 * @param {object} store - Pinia store instance (e.g. useStatementsStore())
 * @param {object} [options]
 * @param {string[]} [options.filterKeys] - Filter keys to sync, e.g. ['status', 'currency']
 * @param {object} [options.codecs] - Per-key URL transforms, e.g. { status: { encode, decode } },
 *   for values that shouldn't be written to the URL verbatim. Keys without a codec copy as-is.
 */

export const STICKY_FILTERS_STORAGE_PREFIX = 'sticky-filters:';

/**
 * Clears every sticky-filters sessionStorage snapshot (all stores, not just one). Intended for
 * use on logout, so one user's leftover filter state can't leak into the next user's session on
 * a shared browser tab.
 */
export function clearStickyFiltersStorage() {
  Object.keys(sessionStorage)
    .filter((key) => key.startsWith(STICKY_FILTERS_STORAGE_PREFIX))
    .forEach((key) => sessionStorage.removeItem(key));
}

export function useFilterUrlSync(store, { filterKeys = [], codecs = {} } = {}) {
  if (!store || !('filters' in store)) {
    return;
  }

  const { filters, setFilters, setAtomic } = store;
  const { params, setParams } = useSearchParams({
    parseOptions: { ignoreQueryPrefix: true, allowDots: true, comma: true },
    stringifyOptions: {
      skipNulls: true,
      allowDots: true,
      arrayFormat: 'comma',
      encoder: encodeValuePreservingCommas,
    },
  });

  const keysToSync = filterKeys;
  const storageKey = `${STICKY_FILTERS_STORAGE_PREFIX}${store.$id}`;

  const persistFiltersToStorage = () => {
    const snapshot = {};
    keysToSync.forEach((key) => {
      snapshot[key] = filters[key];
    });
    sessionStorage.setItem(storageKey, JSON.stringify(snapshot));
  };

  const readFiltersFromStorage = () => {
    try {
      const raw = sessionStorage.getItem(storageKey);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };

  const isSet = (key) => {
    if (key === 'date') {
      // `date` is only "set" once date_from/date_to has a value, since `date` just selects the column.
      return !!(filters.date_from || filters.date_to);
    }

    const value = filters[key];
    return Array.isArray(value) ? value.length > 0 : value !== '' && value != null;
  };

  const syncFiltersToUrl = () => {
    const filtersToSync = {};

    keysToSync.forEach((key) => {
      // Explicitly null out unset filters so setParams's merge with the existing URL
      // clears them, rather than leaving a stale value behind.
      if (!isSet(key)) {
        filtersToSync[key] = null;
        return;
      }

      filtersToSync[key] = codecs[key]?.encode ? codecs[key].encode(filters[key]) : filters[key];
    });

    setParams(filtersToSync);
  };

  onBeforeMount(() => {
    const urlFilters = {};
    let hasUrlFilters = false;

    keysToSync.forEach((key) => {
      if (params[key] === undefined) {
        return;
      }

      hasUrlFilters = true;

      const value = params[key];

      if (codecs[key]?.decode) {
        urlFilters[key] = codecs[key].decode(value);
        return;
      }

      // qs's comma arrayFormat parses a single-item array back as a bare string (e.g.
      // `status=REVIEWED`), so re-wrap it as an array when the store expects one.
      urlFilters[key] = Array.isArray(filters[key]) && !Array.isArray(value) ? [value] : value;
    });

    if (hasUrlFilters) {
      setFilters(urlFilters);

      if (setAtomic) {
        setAtomic('shouldResetFilters', false);
      }

      persistFiltersToStorage();
    } else {
      // No URL params (e.g. a bare sidebar nav after a hard reload) - fall back to storage.
      // Leaves shouldResetFilters alone, so search box/page reset behavior is unaffected.
      const storedFilters = readFiltersFromStorage();
      if (storedFilters) {
        const restoredFilters = {};
        keysToSync.forEach((key) => {
          if (key in storedFilters) {
            restoredFilters[key] = storedFilters[key];
          }
        });
        setFilters(restoredFilters);
      }
    }

    // Sync unconditionally: a remount with stale store filters won't trigger the watch below.
    syncFiltersToUrl();
  });

  watch(
    () => keysToSync.map((key) => filters[key]),
    () => {
      syncFiltersToUrl();
      persistFiltersToStorage();
    }
  );
}
